import { QueryClient } from '@tanstack/react-query'

import { BulkJobStatus, OpsGroup } from '@/models/operations/types'

import { delay } from './actions'
import { listStoreInfo } from './api'
import { getGroup, opsQueryKeys } from './groupsCache'
import {
  isDeploymentStatusBuilding,
  isDeploymentStatusTerminal,
} from './storeUtils'

/** Background poll while any group has stores in BUILDING */
export const DEPLOY_POLL_INTERVAL_MS = 2 * 60 * 1000
export const DEPLOY_POLL_MAX_DURATION_MS = 20 * 60 * 1000

/** Bulk redeploy phase 2 — wait before first store-infra check */
export const BULK_DEPLOY_POLL_FIRST_DELAY_MS = 2 * 60 * 1000
/** Bulk redeploy phase 2 — poll while any target store is BUILDING */
export const BULK_DEPLOY_POLL_INTERVAL_MS = 1 * 60 * 1000

export type DeployPollMode = 'group-no-building' | 'stores-settled'
export type DeployPollResult = 'completed' | 'cancelled' | 'timeout'

export type StartDeployPollOptions = {
  queryClient: QueryClient
  groupId: string
  mode: DeployPollMode
  targetStoreIds?: string[]
  jobId?: string
  firstDelayMs?: number
  intervalMs?: number
  maxDurationMs?: number
  onFinish?: (result: DeployPollResult) => void
}

const activePolls = new Map<string, () => void>()

function patchBulkJob(
  queryClient: QueryClient,
  jobId: string,
  updater: (job: BulkJobStatus) => BulkJobStatus,
): void {
  const job = queryClient.getQueryData<BulkJobStatus>(opsQueryKeys.bulkJob(jobId))
  if (!job) return
  queryClient.setQueryData(opsQueryKeys.bulkJob(jobId), updater(job))
}

function pollRegistryKey(
  options: Pick<StartDeployPollOptions, 'mode' | 'groupId' | 'jobId'>,
): string {
  if (options.mode === 'stores-settled') {
    return `bulk:${options.groupId}:${options.jobId}`
  }
  return `group:${options.groupId}`
}

function countSettledTargetStores(
  group: OpsGroup | undefined,
  targetStoreIds: string[],
): number {
  if (!group) return 0
  const targets = new Set(targetStoreIds)
  return group.stores.filter(
    (store) => targets.has(store.id) && isDeploymentStatusTerminal(store.deployment_status),
  ).length
}

function countBuildingTargetStores(
  group: OpsGroup | undefined,
  targetStoreIds: string[],
): number {
  if (!group) return 0
  const targets = new Set(targetStoreIds)
  return group.stores.filter(
    (store) => targets.has(store.id) && isDeploymentStatusBuilding(store.deployment_status),
  ).length
}

async function pollTick(
  queryClient: QueryClient,
  options: StartDeployPollOptions,
): Promise<'continue' | 'done'> {
  const { groupId, mode, targetStoreIds = [], jobId } = options

  if (mode === 'stores-settled' && targetStoreIds.length === 1) {
    await listStoreInfo(queryClient, groupId, targetStoreIds[0], { silent: true })
  } else {
    await listStoreInfo(queryClient, groupId, undefined, { silent: true })
  }

  const group = getGroup(queryClient, groupId)

  if (mode === 'group-no-building') {
    return (group?.summary.building ?? 0) === 0 ? 'done' : 'continue'
  }

  if (mode === 'stores-settled') {
    const resolved = countSettledTargetStores(group, targetStoreIds)
    const building = countBuildingTargetStores(group, targetStoreIds)

    if (jobId) {
      patchBulkJob(queryClient, jobId, (job) => ({
        ...job,
        deployPollResolved: resolved,
        deployPollTotal: targetStoreIds.length,
      }))
    }

    if (resolved >= targetStoreIds.length) return 'done'
    if (building > 0) return 'continue'
    // API may not show BUILDING yet — keep polling until terminal or timeout.
    return 'continue'
  }

  return 'continue'
}

export function cancelDeployStatusPoll(
  mode: DeployPollMode,
  groupId: string,
  jobId?: string,
): void {
  activePolls.get(pollRegistryKey({ mode, groupId, jobId }))?.()
}

export function isDeployStatusPollActive(
  mode: DeployPollMode,
  groupId: string,
  jobId?: string,
): boolean {
  return activePolls.has(pollRegistryKey({ mode, groupId, jobId }))
}

/** Starts a deploy-status poll. Returns a cancel function. */
export function startDeployStatusPoll(options: StartDeployPollOptions): () => void {
  const key = pollRegistryKey(options)
  activePolls.get(key)?.()

  let cancelled = false
  const cancel = () => {
    cancelled = true
  }
  activePolls.set(key, cancel)

  const intervalMs = options.intervalMs ?? DEPLOY_POLL_INTERVAL_MS
  const firstDelayMs = options.firstDelayMs ?? 0
  const maxDurationMs = options.maxDurationMs ?? DEPLOY_POLL_MAX_DURATION_MS

  void (async () => {
    const startedAt = Date.now()

    try {
      if (firstDelayMs > 0) {
        await delay(firstDelayMs)
      }

      while (!cancelled) {
        if (Date.now() - startedAt > maxDurationMs) {
          options.onFinish?.('timeout')
          return
        }

        try {
          const result = await pollTick(options.queryClient, options)
          if (result === 'done') {
            options.onFinish?.('completed')
            return
          }
        } catch {
          // Keep polling on transient errors
        }

        await delay(intervalMs)
      }

      options.onFinish?.('cancelled')
    } finally {
      if (activePolls.get(key) === cancel) {
        activePolls.delete(key)
      }
    }
  })()

  return cancel
}

/** Bulk redeploy phase 2 — wait 2 min, then poll every 1 min while BUILDING. */
export function pollBulkRedeployDeployStatus(
  queryClient: QueryClient,
  groupId: string,
  targetStoreIds: string[],
  jobId: string,
): Promise<void> {
  if (!targetStoreIds.length) return Promise.resolve()

  return new Promise((resolve) => {
    startDeployStatusPoll({
      queryClient,
      groupId,
      mode: 'stores-settled',
      targetStoreIds,
      jobId,
      firstDelayMs: BULK_DEPLOY_POLL_FIRST_DELAY_MS,
      intervalMs: BULK_DEPLOY_POLL_INTERVAL_MS,
      onFinish: () => resolve(),
    })
  })
}
